import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import Tabs from '@/components/Tabs'
import { AuthType } from '@/components/Tabs/index.type'
import CustomTextInput from '@/components/CustomTextInput'
import CustomButton from '@/components/CustomButton'
// import OtherLoginItems from '@/components/OtherLoginItems'
import { Toast } from 'react-vant';
import { useTranslation } from 'react-i18next'
import { useSendCode } from '@/hooks/useSendCode'
import { APIWeb } from '@/api'

export default React.memo(function Register() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [selectedType, setSelectedType] = useState<AuthType>(AuthType.Email)
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<{ email?: string }>({})
  const { sendCode, sendCodeLoading } = useSendCode()
  // 验证邮箱格式
  const validateEmail = useCallback((emailValue: string) => {
    if (!emailValue) {
      return t('请输入邮箱地址')
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(emailValue)) {
      return t('请输入有效的邮箱地址')
    }
    return ''
  }, [t])

  // 处理邮箱输入变化
  const handleEmailChange = useCallback((value: string) => {
    setEmail(value)
    const error = validateEmail(value)
    setErrors(prev => ({ ...prev, email: error }))
  }, [validateEmail])

  // 切换登录注册类型
  const onChange = useCallback((type: AuthType) => {
    if (type === AuthType.Phone) {
      Toast.info({
        message: t('当前地区暂不支持手机号登录注册'),
        position: 'middle'
      })
      return
    }
    setSelectedType(type)
  }, [t])

  // 检查表单是否有效
  const isFormValid = useMemo(() => {
    if (selectedType === AuthType.Email) {
      return email && !validateEmail(email)
    }
    return false // 手机注册暂未实现
  }, [selectedType, email, validateEmail])

  // 处理注册提交
  const handleSendCode = useCallback(async () => {
    if (selectedType === AuthType.Email) {
      const emailError = validateEmail(email)
      if (emailError) {
        setErrors(prev => ({ ...prev, email: emailError }))
        return
      }
      
      // 发送验证码前检查账号是否已注册
      try {
        Toast.loading({message: t('验证中'),forbidClick: true})
        const response = await APIWeb.checkAccountStatus({ account: email })
        Toast.clear()
        if (response?.registered) {
          Toast.info(t('该邮箱已被注册'))
          return
        }
      } catch (error) {
        console.error('检测账号状态失败:', error)
        // 检测失败时允许继续发送验证码
      }
      
      const data = await sendCode({ account: email, scene: 'register' })
      if (data) {
        setEmail('')
        navigate(`/register/verification-code`, {
          state: data
        })
      }
    }
  }, [navigate, selectedType, email, validateEmail, sendCode, t])

  const EmailRegisterView = useMemo(
    () => (
      <div className="space-y-2">
        <div className="text-sm text-[#1A1A1A]/60 mb-1">{t('您的电子邮箱')}</div>
        <CustomTextInput
          isErrored={Boolean(errors.email)}
          value={email}
          onChangeText={handleEmailChange}
          placeholder={t('请输入您的电子邮箱')}
          inputMode='email'
        />
        {errors.email && (
          <p className="mt-1 text-xs text-red-600">{errors.email}</p>
        )}
      </div>
    ),
    [email, handleEmailChange, errors.email, t]
  )

  const PhoneRegisterView = useMemo(
    () => (
      <div className="text-5xl text-gray-200 text-center my-20">
        {t('即将上线')}
      </div>
    ),
    [t]
  )

  const RegisterViewRender = useMemo(
    () => (
      selectedType === AuthType.Email ? EmailRegisterView : PhoneRegisterView
    ),
    [selectedType, EmailRegisterView, PhoneRegisterView]
  )

  return (
    <div className="bg-white h-full">
      <div className="overflow-y-auto">
        <div>
          {/* Logo */}

          <div className="w-full px-6 text-center text-[#1A1A1A] flex flex-col items-center justify-center pt-14 pb-8 text-2xl">
            <img className='w-24 mb-5' src="/icon/logo.svg" alt="logo" />
            {t('创建RFPAY账户')}
          </div>

          <div className="px-6">
            <Tabs
              items={[
                { type: AuthType.Email, text: t('邮箱注册') },
                { type: AuthType.Phone, text: t('手机注册') }
              ]}
              defaultSelected={AuthType.Email}
              onChange={onChange}
            />
          </div>

          <div className="px-6 mt-8">
            {RegisterViewRender}
          </div>

          <div className="px-6 mt-8">
            <CustomButton
              onClick={handleSendCode}
              loading={sendCodeLoading}
              disabled={sendCodeLoading || !isFormValid}
            >
              {t('注册')}
            </CustomButton>
          </div>

          <div className="px-6 mt-6 flex items-center justify-center space-x-2">
            <span className="text-sm text-gray-600">{t('已有账户')}</span>
            <Link to="/login" className="text-sm text-[var(--primary-color)]">
              {t('登录')}
            </Link>
          </div>
        </div>
        {/* Other register options */}
        {/* <div className="px-6 mt-12 pb-8">
          <OtherLoginItems />
        </div> */}
      </div>
    </div>
  )
})